import { Tooltip } from "@mui/material";
import { InPlaceEditorProps, Labeled, TextField, TextInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";

/**
 * In-place editor for text-based fields (e.g. plain text, email).
 *
 * Renders a read view using {@link TextField} and allows inline editing using {@link TextInput}.
 * Supports custom input types such as `"email"` via the `type` prop.
 * Wrapped in a {@link Labeled} component to optionally display a label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label for the field
 * @param type - Optional input type (e.g. "text", "email")
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link TextInput}
 */
const InPlaceTextEditor = ({
  source,
  label,
  type = "text",
  readOnly = false,
  readOnlyReason,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  type?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Tooltip title={readOnly ? readOnlyReason : undefined}>
        <span>
          <Labeled label={label}>
            <TextField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
          </Labeled>
        </span>
      </Tooltip>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <TextInput
            {...rest}
            source={source}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            type={type}
            validate={validate}
            autoFocus
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <TextField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceTextEditor;
